import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  centsToDollarsNumber,
  displayUSDValueForRegion,
  expectUnreachable,
  getExperimentalTotalNanoPrice,
  getLocalizedPricing,
  getPriceForRateUnit,
  RateUnit,
} from '../../lib/util';
import * as types from '../../types';
import { miniCollarId, series3PlusCollarId, ultraCollarId } from '../../types';
import styles from './SubscriptionOptionDetails.module.scss';
import { showNanoVariant } from '../NanoBanner/NanoBanner';
import { showRescueGwaVariant } from '../../views/RescueLanding/RescueBanner';
import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';
import { getNanoReferralPetNameCookie } from '../../lib/util/nanoReferralPetNameCookie';
import { FiCountry, useLocale } from '../../contexts/LocaleContext';
import { useIsGift } from '../../lib/util/isGift';
import { Flex, Text } from '@mantine/core';
import FreeTrialLearnMoreModal from '../FreeTrialLearnMoreModal';
import * as events from '../../lib/analytics/events';
import { getFreeTrialDurationLabel } from '../../lib/subscription';
import useFreeTrialShippingPrice from '../../hooks/useFreeTrialShippingPrice';
import useFreeTrialSubscriptionPrice from '../../hooks/useFreeTrialSubscriptionPrice';
import { ReactComponent as IconWarrantyOutline } from '../../assets/images/icons/icon_warranty_outline.svg';

export interface SubscriptionOption {
  billingCadence: types.BillingCadence;
  billingPeriodsPerTerm: number;
  buyItMembership?: boolean;
  displayedRate: string;
  displayedRateUnit: string;
  freeTrial?: boolean;
  freeTrialDays?: number;
  name: string;
  /** @deprecated use `pricingInCents` instead */
  priceInCents: number;
  pricingInCents: types.PricingByCurrency;
  renewalMonths: number;
  renewalYears?: number | null;
  savings?: string | null;
  monthlyUpgradeSavings?: string | null;
  sku: string;
  upsellOnly?: boolean;
  valueLabel?: string | null;
  supportPlus?: boolean;
  couponExtensionMonths?: number | null;
  planSwitchCreditAppliedInCents?: number | null;
  planSwitchAmountChargedInCents?: number | null;
  activationFeePricingInCents?: types.PricingByCurrency | null;
}

interface SubscriptionOptionButtonProps {
  subscriptionProduct: SubscriptionOption;
  renewalDate?: DateTime;
  expirationDate?: DateTime;
  pauseDate?: DateTime;
  disableSavingsDisplay?: boolean;
  overrideTitle?: string;
  overrideSubtitle?: string;
  pricingRateUnit?: RateUnit;
  renewScreens?: boolean;
  monthlyUpgrade?: boolean;
  showSubscriptionLengthDiscount?: boolean;
  prominentPricingDesign?: boolean;
  isPaused?: boolean;
  redemptionType?: types.RedemptionType;
  hasValidRedemptionCode?: boolean;
  /** Resolved dollar discount of the redemption code; drives the net price + "Save $X" badge. */
  redemptionDiscountInCents?: number | null;
  isReferral?: boolean;
  isReferralV2?: boolean;
  productCategory?: types.ProductCategory;
  onStartFreeTrial?: () => void;
  /**
   * Discount in cents for this subscription under the currently active weekly
   * promo, sourced from `/api/promotions/code-details`. `undefined` means no
   * matching promo (or still loading) — the badge is hidden in that case.
   */
  promoDiscountInCents?: number;
}

function renewalTitle(renewalMonths: number, prominentPricingDesign?: boolean, t?: TFunction) {
  if (!t) return '';
  if (renewalMonths % 12 === 0) {
    const years = Math.round(renewalMonths / 12);
    return t('subscription_years_year', { years });
  }

  if (renewalMonths === 1) {
    return prominentPricingDesign ? t('subscription_month_to_month') : t('subscription_monthly');
  }

  return t('subscription_months', { months: renewalMonths });
}

function elevatedNanoOfferDescription(subscription: SubscriptionOption, t: TFunction) {
  return t('plus_activation_fee');
}

function billingCadenceDescription(
  subscription: SubscriptionOption,
  country: FiCountry,
  currency: types.FiCurrency,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
) {
  let pricePeriod = '';
  const priceInCents = getLocalizedPricing(subscription.pricingInCents, currency);
  const price = currencyFormatter.format(centsToDollarsNumber(priceInCents));
  if (subscription.renewalMonths === 1) {
    pricePeriod = t('subscription_price_per_month', { price });
  } else if (subscription.renewalMonths === 6) {
    pricePeriod = t('subscription_price_semi_annually', { price });
  } else if (subscription.renewalMonths % 12 === 0) {
    const years = Math.round(subscription.renewalMonths / 12);
    if (years === 1) {
      pricePeriod = t('subscription_price_annually', { price });
    } else if (years === 2) {
      pricePeriod = t('subscription_price_biennially', { price });
    } else {
      pricePeriod = t('subscription_price_every_years', { price, years });
    }
  } else {
    pricePeriod = t('subscription_price_every_months', { price, months: subscription.renewalMonths });
  }

  return t('billed_as_price_period', { pricePeriod });
}

function nextSignificantDateDescription(
  renewScreens: boolean,
  isPaused: boolean,
  renewalDate?: DateTime,
  expirationDate?: DateTime,
  pauseDate?: DateTime,
  t?: TFunction,
) {
  if (!t) return null;
  const DATE_FORMAT_TOKEN = 'LL/dd/yy, h:mma';
  if (renewScreens && expirationDate) {
    return (
      <div className={styles.description}>
        {t('plan_expires_on_date', { date: expirationDate.toFormat(DATE_FORMAT_TOKEN) })}
      </div>
    );
  }

  if (isPaused && renewalDate && pauseDate) {
    const pauseDateIsInFuture = pauseDate > DateTime.now();

    return (
      <div className={styles.description}>
        {pauseDateIsInFuture
          ? t('membership_will_pause_on_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })
          : t('membership_will_resume_on_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })}
      </div>
    );
  }

  if (renewalDate) {
    return (
      <div className={styles.description}>{t('renews_date', { date: renewalDate.toFormat(DATE_FORMAT_TOKEN) })}</div>
    );
  }
  return null;
}

function billingRateFragment(
  nanoVariantWithElevatedOffer: boolean,
  showTotalPriceFormat: boolean,
  country: FiCountry,
  currency: types.FiCurrency,
  currencyFormatter: Intl.NumberFormat,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
  displayRateUnit: string,
  displayRate: string,
  priceInCents: number,
  overrideSubtitle: string | undefined,
  renewalMonths: number,
  t: TFunction,
) {
  if (nanoVariantWithElevatedOffer) {
    return (
      <div className={styles.price}>
        <span className={styles.priceBold}>{displayRate + ' '}</span>
        <span className={classNames(styles.priceBold, styles.priceBoldCrossed)}>
          {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
      </div>
    );
  }

  if (showTotalPriceFormat) {
    return (
      <div className={styles.price}>
        <span className={styles.priceBold}>
          {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.price}>
      {overrideSubtitle ? (
        <Text size={16} fw={600} lh={1.4}>
          {overrideSubtitle}
        </Text>
      ) : (
        <>
          <span className={styles.priceBold}>{displayUSDValueForRegion(country, currency, displayRate)}</span>
          {' / '}
          {t(displayRateUnit)}
        </>
      )}
    </div>
  );
}

function billingCadenceDescriptionFragment(
  nanoVariantWithElevatedOffer: boolean,
  totalPriceSubscriptionEnabled: boolean,
  country: FiCountry,
  currency: types.FiCurrency,
  displayRateUnit: string,
  displayRate: string,
  subscriptionProduct: SubscriptionOption,
  t: TFunction,
  currencyFormatter: Intl.NumberFormat,
) {
  if (nanoVariantWithElevatedOffer) {
    return (
      <>
        <div className={styles.description}>{elevatedNanoOfferDescription(subscriptionProduct, t)}</div>
      </>
    );
  }

  if (totalPriceSubscriptionEnabled) {
    return (
      <div className={styles.description}>
        {t('equal_to_price_per_week', { price: displayUSDValueForRegion(country, currency, displayRate) })}
      </div>
    );
  }

  return (
    <div className={styles.description}>
      {billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
    </div>
  );
}

export function MonthlySubscriptionContent({
  subscriptionProduct,
  renewalDate,
  expirationDate,
  pauseDate,
  disableSavingsDisplay,
  overrideTitle,
  overrideSubtitle,
  pricingRateUnit = 'week',
  renewScreens = false,
  monthlyUpgrade = false,
  showSubscriptionLengthDiscount = false,
  prominentPricingDesign = false,
  isPaused = false,
  isReferral = false,
  isReferralV2 = false,
  promoDiscountInCents,
}: SubscriptionOptionButtonProps) {
  const isGift = useIsGift();
  const { t } = useTranslation();
  const { country, currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const subscriptionPriceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
  const activationFeeInCents =
    isReferralV2 && subscriptionProduct.activationFeePricingInCents
      ? getLocalizedPricing(subscriptionProduct.activationFeePricingInCents, currency)
      : 0;
  // `promoDiscountInCents` is the full referral savings (membership discount +
  // activation-fee waiver), so the discounted price must be computed off the same
  // base as the crossed-out price (membership + activation fee) — otherwise the
  // activation fee gets double-counted and the price reads $20 too low.
  const discountedPriceInCents = subscriptionPriceInCents + activationFeeInCents - (promoDiscountInCents ?? 0);
  const totalReferralSavingsInCents = promoDiscountInCents ?? 0;
  // Don't show nano ecomm test variant on in-app collar management screens
  const nanoEcomTestTwoEnabled =
    !window.location.pathname.startsWith('/subscription') &&
    !window.location.pathname.startsWith('/redeem') &&
    !window.location.pathname.startsWith('/vip') &&
    (showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-2', false));

  const nanoVariantWithElevatedOffer =
    showRescueGwaVariant() ||
    (showNanoVariant() && getFiGrowthBook().getFeatureValue('nano-elevated-promo-offer', false));

  const isFreeTrialEnabled = getFiGrowthBook().getFeatureValue<boolean>('free-trial-enabled', false);
  const showTotalPriceFormat = isFreeTrialEnabled || country === FiCountry.CA;

  const { unit: displayRateUnit, displayRate } = nanoVariantWithElevatedOffer
    ? getExperimentalTotalNanoPrice(subscriptionProduct)
    : getPriceForRateUnit(subscriptionProduct, pricingRateUnit, currency, currencyFormatter);
  const priceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);

  let prepaidPeriodTitle: string | undefined =
    overrideTitle ?? renewalTitle(subscriptionProduct.renewalMonths, prominentPricingDesign, t);

  if (nanoEcomTestTwoEnabled && subscriptionProduct.renewalMonths === 1) {
    prepaidPeriodTitle = undefined;
  }

  if (showTotalPriceFormat && subscriptionProduct.renewalMonths === 1) {
    prepaidPeriodTitle = t('subscription_monthly');
  }

  const savingsAmount = monthlyUpgrade ? subscriptionProduct.monthlyUpgradeSavings : subscriptionProduct.savings;
  const savingsPill = savingsAmount && !disableSavingsDisplay && (
    <div className={styles.savingsPill}>{savingsAmount}</div>
  );

  const promoDiscountDisplay = formatPromoDiscountDisplay(
    promoDiscountInCents,
    subscriptionProduct.renewalMonths,
    wholeNumberCurrencyFormatter,
  );

  const saleBadge = useFeatureValue('ecom-bfcm-badge', 0);
  const blackFridayDesign = saleBadge === 1;
  const cyberMondayDesign = saleBadge === 2;

  // 50/50 growthbook experiment to shift checkouts toward 1yr/2yr plans: add a
  // "best value" badge to the 2-year card and stop advertising the promo savings
  // on the 6-month card (the discount still applies in cart). short-circuit to the
  // two affected cards so getFeatureValue (and its exposure tracking) only fires
  // when a card the experiment actually changes is rendered.
  const inBestValueExperiment =
    (subscriptionProduct.renewalMonths === 6 || subscriptionProduct.renewalMonths === 24) &&
    getFiGrowthBook().getFeatureValue<boolean>('ecom-2yr-best-value-badge', false);
  const show2yrBestValueBadge = inBestValueExperiment && subscriptionProduct.renewalMonths === 24;
  // the rescue guard keeps a future best-value launch from silently hiding the adoption discount
  const hide6moPromoBadge = inBestValueExperiment && subscriptionProduct.renewalMonths === 6 && !showRescueGwaVariant();
  // the rescue variant states the amount in the offer box at the top of the page instead, so the
  // card-level pill would say the same thing twice
  const promoBadgeMovedToOfferBox = showRescueGwaVariant();

  return (
    <div
      className={classNames(styles.container, {
        [styles.prominentPricingDesign]: prominentPricingDesign,
        [styles.blackFridayDesign]: blackFridayDesign,
        [styles.cyberMondayDesign]: cyberMondayDesign,
      })}
    >
      <div className={styles.column}>
        {subscriptionProduct.renewalMonths === 12 && <div className={styles.mostPopularPill}>{t('most_popular')}</div>}
        {show2yrBestValueBadge && <div className={styles.bestValuePill}>{t('best_value')}</div>}
        {prepaidPeriodTitle && (
          <div className={classNames(styles.row, styles.title)}>
            {prepaidPeriodTitle && (
              <div className={styles.billingCadence} style={{ fontSize: overrideTitle ? 20 : undefined }}>
                {prepaidPeriodTitle}
              </div>
            )}
            {isReferralV2 && totalReferralSavingsInCents > 0 && (
              <div className={styles.referralSavingsPill}>
                {t('referral_v2_saved_from_invite', {
                  amount: currencyFormatter.format(centsToDollarsNumber(totalReferralSavingsInCents)),
                })}
              </div>
            )}
            {prominentPricingDesign && savingsPill}
            {!isReferralV2 &&
              !hide6moPromoBadge &&
              !promoBadgeMovedToOfferBox &&
              showTotalPriceFormat &&
              showSubscriptionLengthDiscount &&
              promoDiscountDisplay !== null && (
                <div className={styles.couponExtension}>
                  <span className={styles.couponDescription}>
                    {t('save_amount_with_promo', { amount: promoDiscountDisplay })}
                  </span>
                </div>
              )}
          </div>
        )}
        {(isReferral || isReferralV2) && totalReferralSavingsInCents > 0 ? (
          <>
            <div className={styles.price}>
              <span className={classNames(styles.priceBold, styles.priceBoldCrossedSameSize)}>
                {currencyFormatter.format(centsToDollarsNumber(priceInCents + activationFeeInCents))}
              </span>{' '}
              <span className={styles.priceBold}>
                {currencyFormatter.format(centsToDollarsNumber(discountedPriceInCents))}
              </span>
            </div>
            <div className={styles.description}>
              {isReferralV2
                ? t('equal_to_price_per_week', {
                    price: displayUSDValueForRegion(country, currency, displayRate),
                  })
                : billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
            </div>
          </>
        ) : prominentPricingDesign ? (
          // isUpgradePath || nanoVariantEnabled ? false : https://app.growthbook.io/features/ecom-prominent-pricing
          // All other parent components are not using the prominentPricingDesign flag.
          <div className={styles.price}>
            <span
              className={classNames(styles.priceBold, {
                [styles.monthToMonthPrice]: subscriptionProduct.renewalMonths === 1,
              })}
            >
              {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
              {subscriptionProduct.renewalMonths === 1 && '/mo'}
            </span>{' '}
            {subscriptionProduct.renewalMonths > 1 && (
              <span className={styles.description}>
                {t('as_low_as_price_per_unit', {
                  price: displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate), // TODO: move away from this?
                  unit:
                    subscriptionProduct.displayedRateUnit === 'month' ? 'mo' : subscriptionProduct.displayedRateUnit,
                })}
              </span>
            )}
          </div>
        ) : (
          // This path is the only live path in the tertiary conditional.
          <>
            {billingRateFragment(
              nanoVariantWithElevatedOffer,
              showTotalPriceFormat,
              country,
              currency,
              currencyFormatter,
              wholeNumberCurrencyFormatter,
              displayRateUnit,
              displayRate,
              priceInCents,
              overrideSubtitle,
              subscriptionProduct.renewalMonths,
              t,
            )}
            {!isGift &&
              billingCadenceDescriptionFragment(
                nanoVariantWithElevatedOffer,
                showTotalPriceFormat,
                country,
                currency,
                displayRateUnit,
                displayRate,
                subscriptionProduct,
                t,
                currencyFormatter,
              )}
          </>
        )}
        {nextSignificantDateDescription(renewScreens, isPaused, renewalDate, expirationDate, pauseDate, t)}
      </div>
      <Flex justify="flex-end" align="flex-start" sx={{ whiteSpace: 'nowrap' }}>
        {prominentPricingDesign ? (
          <>
            {!hide6moPromoBadge &&
              !promoBadgeMovedToOfferBox &&
              showSubscriptionLengthDiscount &&
              promoDiscountDisplay !== null && (
                <div className={styles.couponExtension}>
                  <span className={styles.description}>
                    {promoDiscountDisplay}
                    {nanoVariantWithElevatedOffer ? '' : ' ' + t('off')}
                  </span>
                  <span className={styles.title}>{t('exclusive_offer')}</span>
                </div>
              )}
          </>
        ) : (
          <>
            {!hide6moPromoBadge &&
              !promoBadgeMovedToOfferBox &&
              !showTotalPriceFormat &&
              showSubscriptionLengthDiscount &&
              promoDiscountDisplay !== null && (
                <div className={styles.couponExtension}>
                  {!isGift && (
                    <span className={styles.title}>
                      {blackFridayDesign
                        ? t('black_fi_day_caps')
                        : cyberMondayDesign
                          ? t('cyber_monday_caps')
                          : t('exclusive_offer')}
                    </span>
                  )}
                  <span className={styles.description}>
                    {promoDiscountDisplay}
                    {nanoVariantWithElevatedOffer ? '' : ' ' + t('off')}
                  </span>
                </div>
              )}
          </>
        )}
      </Flex>
      {(renewScreens || isPaused) && (
        <div className={classNames(styles.column, styles.right)}>
          <span className={classNames(styles.valueLabel, styles.deactivated)}>
            {renewScreens ? t('canceled') : t('paused')}
          </span>
        </div>
      )}
    </div>
  );
}

export function YearlySubscriptionContent({
  subscriptionProduct,
  renewalDate,
  expirationDate,
  pauseDate,
  disableSavingsDisplay,
  overrideTitle,
  renewScreens = false,
  isPaused = false,
}: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const { country, currency, currencyFormatter } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>
            {overrideTitle ?? renewalTitle(subscriptionProduct.renewalMonths, undefined, t)}
          </div>
          {subscriptionProduct.savings && !disableSavingsDisplay && (
            <div className={styles.savingsPill}>{subscriptionProduct.savings}</div>
          )}
        </div>
        <div className={styles.price}>
          <span className={styles.priceBold}>
            {displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate)}
          </span>{' '}
          {'/'}
          {subscriptionProduct.displayedRateUnit}
        </div>
        <div className={styles.description}>
          {billingCadenceDescription(subscriptionProduct, country, currency, t, currencyFormatter)}
        </div>
        {nextSignificantDateDescription(renewScreens, isPaused, renewalDate, expirationDate, pauseDate, t)}
      </div>
      {renewScreens || isPaused ? (
        <div className={classNames(styles.column, styles.right)}>
          <span className={classNames(styles.valueLabel, styles.deactivated)}>
            {renewScreens ? t('canceled') : t('paused')}
          </span>
        </div>
      ) : (
        <div className={styles.column}></div>
      )}
    </div>
  );
}

export function BuyItSubscriptionContent({ subscriptionProduct }: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const { country, currency } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('buy_it')}</div>
        </div>
        <div className={styles.price}>
          {displayUSDValueForRegion(country, currency, subscriptionProduct.displayedRate)}
        </div>
        <div className={styles.description}>{t('one_time_payment')}</div>
      </div>
      <div className={classNames(styles.column, styles.right)}>
        <div className={styles.supportPlusWrapper}>
          {subscriptionProduct.supportPlus && (
            <>
              <div className={classNames(styles.supportLabel)}>{t('support_plus_included')}</div>
              <div className={classNames(styles.supportDescription)}>{t('prioritized_phone_support')}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function RedemptionSubscriptionContent({
  subscriptionProduct,
  hasValidRedemptionCode,
  redemptionType,
  redemptionDiscountInCents,
}: SubscriptionOptionButtonProps) {
  const { t } = useTranslation();
  const siteConfig = useSelector((state: types.AppState) => state.config.siteConfig);
  const { currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();

  // Check if this is a GwP free trial
  const isGwPTrial = redemptionType === 'gwp_free_trial';

  const gwpFreeTrialMonths = siteConfig.gwpFreeTrialMonths;

  const title = renewalTitle(subscriptionProduct.renewalMonths).toUpperCase();

  const priceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
  // Callers that don't resolve an amount (the free-trial VIP flow) keep the historical
  // fully-covered display; a resolved dollar amount shows the actual net (e.g. $20 when a
  // $99 code applies to the $119 plan).
  const discountInCents = redemptionDiscountInCents ?? priceInCents;
  const netPriceInCents = Math.max(0, priceInCents - discountInCents);

  const redemptionBillingRateFragment = (
    <div className={styles.price}>
      {hasValidRedemptionCode ? (
        <>
          <span className={classNames(styles.priceBold, styles.priceBoldCrossed)}>
            {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
          </span>
          <span className={styles.priceBold}>{currencyFormatter.format(centsToDollarsNumber(netPriceInCents))}</span>
        </>
      ) : (
        <span className={classNames(styles.priceBold)}>
          {currencyFormatter.format(centsToDollarsNumber(priceInCents))}
        </span>
      )}
    </div>
  );
  const redemptionBillingCadenceDescriptionFragment = (
    <div className={styles.description}>{t('plus_activation_fee')}</div>
  );

  return (
    <div className={classNames(styles.container, styles.redemptionContainer)}>
      <div className={styles.column}>
        {redemptionType !== 'gwp_free_trial' && (
          <div className={classNames(styles.row, styles.title)}>
            <div className={styles.billingCadence}>{title}</div>
          </div>
        )}
        {redemptionBillingRateFragment}
        {redemptionBillingCadenceDescriptionFragment}
      </div>
      <div className={classNames(styles.column, styles.right)}>
        <div className={styles.couponExtension}>
          <span className={styles.title}>
            {hasValidRedemptionCode
              ? isGwPTrial
                ? t('exclusive_offer_applied')
                : t('save_amount_with_promo', {
                    amount: wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discountInCents)),
                  })
              : isGwPTrial
                ? t('apply_code_at_checkout_for_months_free', { months: gwpFreeTrialMonths })
                : t('apply_code_at_checkout_for_amount_off', {
                    amount: currencyFormatter.format(99),
                  })}
          </span>
        </div>
      </div>
    </div>
  );
}

interface FreeTrialSubscriptionContentProps {
  freeTrialDays: number;
  productCategory: types.ProductCategory | undefined;
  onStartFreeTrial: (() => void) | undefined;
  isReferralV2?: boolean;
}

export function FreeTrialSubscriptionContent({
  freeTrialDays,
  productCategory,
  onStartFreeTrial,
  isReferralV2 = false,
}: FreeTrialSubscriptionContentProps) {
  const { t } = useTranslation();
  const collarProductId =
    productCategory === types.ProductCategory.MINI
      ? miniCollarId
      : productCategory === types.ProductCategory.COLLAR_KIT
        ? series3PlusCollarId
        : productCategory === types.ProductCategory.ULTRA
          ? ultraCollarId
          : null;

  const shippingPrice = useFreeTrialShippingPrice();
  const subscriptionPrice = useFreeTrialSubscriptionPrice(collarProductId);

  const descriptionKey =
    productCategory === types.ProductCategory.MINI
      ? 'free_trial_description_mini'
      : productCategory === types.ProductCategory.COLLAR_KIT
        ? 'free_trial_description_series3'
        : undefined;

  return (
    <div className={styles.container}>
      {isReferralV2 && <div className={styles.referralPill}>{t('referral_v2_recommended_for_you')}</div>}
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title, styles.freeTrialTitleRow)}>
          <div className={styles.billingCadence}>
            {t('free_trial_title', { duration: getFreeTrialDurationLabel(freeTrialDays) })}
          </div>
          <div className={styles.premiumPreOwnedBadge}>
            <IconWarrantyOutline />
            {t('risk_free_for_30_days')}
          </div>
        </div>
        {descriptionKey && shippingPrice && (
          <div className={styles.freeTrialDescription}>{t(descriptionKey, { shippingPrice })}</div>
        )}
        {shippingPrice && subscriptionPrice && (
          <FreeTrialLearnMoreModal
            trigger={
              <span className={styles.learnMoreUppercase} onClick={(e) => e.stopPropagation()}>
                {t('free_trial_learn_more')} {'→'}
              </span>
            }
            onStartFreeTrial={() => {
              events.startFreeTrialClickedFromLearnMoreModal();
              onStartFreeTrial?.();
            }}
            onOpen={() => events.freeTrialLearnMoreClickedFromSubscriptionOption()}
            subscriptionPrice={subscriptionPrice}
            productCategory={productCategory}
          />
        )}
      </div>
    </div>
  );
}

export function SubscriptionOverrideContent() {
  const { t } = useTranslation();
  const { currencyFormatter } = useLocale();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('subscription_override')}</div>
        </div>
        <div className={styles.price}>{currencyFormatter.format(0)}</div>
        <div className={styles.description}>{t('renews_indefinitely')}</div>
      </div>
      <div className={styles.column}></div>
    </div>
  );
}

export function LifetimeSubscriptionContent() {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <div className={styles.column}>
        <div className={classNames(styles.row, styles.title)}>
          <div className={styles.billingCadence}>{t('lifetime_membership')}</div>
        </div>
        <div className={styles.price}></div>
        <div className={styles.description}>{t('renews_indefinitely')}</div>
      </div>
      <div className={styles.column}></div>
    </div>
  );
}

export default function SubscriptionOptionButton({
  disableSavingsDisplay,
  renewalDate,
  expirationDate,
  pauseDate,
  subscriptionProduct,
  overrideTitle,
  overrideSubtitle,
  renewScreens,
  monthlyUpgrade = false,
  showSubscriptionLengthDiscount = false,
  prominentPricingDesign = false,
  isPaused = false,
  redemptionType = null,
  hasValidRedemptionCode = false,
  redemptionDiscountInCents = null,
  isReferral = false,
  isReferralV2 = false,
  productCategory,
  onStartFreeTrial,
  promoDiscountInCents,
}: SubscriptionOptionButtonProps) {
  // This can be undefined if we're not wrapped in a GrowthBookProvider. We only use the GrowthBookProvider in
  // the main store flow so in-app webviews won't have this.
  const growthBookEnabled = useIsGrowthBookEnabled();

  if (subscriptionProduct.freeTrial && subscriptionProduct.freeTrialDays) {
    return (
      <FreeTrialSubscriptionContent
        freeTrialDays={subscriptionProduct.freeTrialDays}
        productCategory={productCategory}
        onStartFreeTrial={onStartFreeTrial}
        isReferralV2={isReferralV2}
      />
    );
  } else if (redemptionType) {
    return (
      <RedemptionSubscriptionContent
        subscriptionProduct={subscriptionProduct}
        hasValidRedemptionCode={hasValidRedemptionCode}
        redemptionType={redemptionType}
        redemptionDiscountInCents={redemptionDiscountInCents}
      />
    );
  } else if (subscriptionProduct.buyItMembership) {
    return <BuyItSubscriptionContent subscriptionProduct={subscriptionProduct} />;
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Month) {
    const monthlySubscriptionContent = (
      <MonthlySubscriptionContent
        disableSavingsDisplay={disableSavingsDisplay}
        renewalDate={renewalDate}
        expirationDate={expirationDate}
        pauseDate={pauseDate}
        overrideTitle={overrideTitle}
        overrideSubtitle={overrideSubtitle}
        subscriptionProduct={subscriptionProduct}
        pricingRateUnit={'week'}
        renewScreens={renewScreens}
        monthlyUpgrade={monthlyUpgrade}
        showSubscriptionLengthDiscount={showSubscriptionLengthDiscount}
        prominentPricingDesign={prominentPricingDesign}
        isPaused={isPaused}
        isReferral={isReferral}
        isReferralV2={isReferralV2}
        promoDiscountInCents={promoDiscountInCents}
      />
    );

    return growthBookEnabled ? <FeaturesReady>{monthlySubscriptionContent}</FeaturesReady> : monthlySubscriptionContent;
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Year) {
    return (
      <YearlySubscriptionContent
        disableSavingsDisplay={disableSavingsDisplay}
        renewalDate={renewalDate}
        expirationDate={expirationDate}
        pauseDate={pauseDate}
        overrideTitle={overrideTitle}
        subscriptionProduct={subscriptionProduct}
        renewScreens={renewScreens}
        isPaused={isPaused}
      />
    );
  } else if (subscriptionProduct.billingCadence === types.BillingCadence.Week) {
    // We don't support weekly subscriptions yet as they are currently only used by suplement subscriptions
    return null;
  } else {
    expectUnreachable(subscriptionProduct.billingCadence);
    return null;
  }
}

/**
 * Nano-elevated is a separate experiment on its own flag; it overrides the
 * server-sourced promo amount with a hardcoded $99 and bakes " off" into
 * the returned string to match the pre-existing call-site expectation.
 *
 * Exported for the rescue offer box, which shows the same amount this card's badge would - one
 * source, so the two can never disagree.
 */
export function formatPromoDiscountDisplay(
  promoDiscountInCents: number | undefined,
  renewalMonths: number,
  wholeNumberCurrencyFormatter: Intl.NumberFormat,
): string | null {
  const showNanoOffer = showNanoVariant() || !!getNanoReferralPetNameCookie();
  const elevatedNanoOffer = getFiGrowthBook().getFeatureValue<boolean | null>('nano-elevated-promo-offer', false);

  if (showRescueGwaVariant() || (showNanoOffer && elevatedNanoOffer)) {
    return [6, 12, 24].includes(renewalMonths) ? `${wholeNumberCurrencyFormatter.format(99)}` : null;
  }

  if (!promoDiscountInCents) return null;
  return wholeNumberCurrencyFormatter.format(centsToDollarsNumber(promoDiscountInCents));
}
